import { buildApiUrl, buildAssetUrl, withBusinessHeaders } from './apiConfig';

const DAILY_ENTRIES_API_URL =
  process.env.REACT_APP_DAILY_ENTRIES_API_URL || buildApiUrl('/daily-entries');
const EXPIRED_PROOF_IMAGE_VALUE = 'expired';

export async function fetchDailyEntries(filters = {}) {
  const query = new URLSearchParams();

  if (filters.type && filters.type !== 'all') {
    query.set('type', filters.type);
  }

  if (filters.date) {
    query.set('date', filters.date);
  }

  const url = query.toString()
    ? `${DAILY_ENTRIES_API_URL}?${query.toString()}`
    : DAILY_ENTRIES_API_URL;
  const response = await requestDailyEntryApi(url, {
    fallbackMessage: 'Unable to load daily entries right now.',
  });

  return (await response.json()).map(normalizeDailyEntry);
}

export async function createDailyEntry(payload) {
  const response = await requestDailyEntryApi(DAILY_ENTRIES_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to save daily entry right now.',
  });

  return normalizeDailyEntry(await response.json());
}

export async function updateDailyEntry(id, payload) {
  const response = await requestDailyEntryApi(`${DAILY_ENTRIES_API_URL}/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    fallbackMessage: 'Unable to update daily entry right now.',
  });

  return normalizeDailyEntry(await response.json());
}

export async function deleteDailyEntry(id) {
  const response = await requestDailyEntryApi(`${DAILY_ENTRIES_API_URL}/${id}`, {
    method: 'DELETE',
    fallbackMessage: 'Unable to delete daily entry right now.',
  });

  return normalizeDailyEntry(await response.json());
}

function normalizeDailyEntry(entry) {
  const proofImage = entry.proofImage || '';
  const isProofExpired = proofImage === EXPIRED_PROOF_IMAGE_VALUE;

  return {
    id: entry.id,
    type: entry.type,
    date: entry.date,
    supplier: entry.supplier || '',
    quantity: entry.quantity || '',
    netKg: entry.netKg || '',
    lansa: entry.type === 'cash' ? '-' : entry.lansa || '',
    caAmount: entry.caAmount || '',
    bank: entry.bank || '',
    proofPreview: proofImage && !isProofExpired ? buildAssetUrl(proofImage) : '',
    proofImage,
    proofImageUploadedAt: entry.proofImageUploadedAt || '',
    proofImageExpiresAt: entry.proofImageExpiresAt || '',
    proofImageDaysRemaining: entry.proofImageDaysRemaining,
    isProofExpired,
  };
}

async function requestDailyEntryApi(url, options) {
  const { fallbackMessage, ...requestOptions } = options;
  let response;

  try {
    response = await fetch(url, {
      ...requestOptions,
      headers: withBusinessHeaders(requestOptions.headers),
    });
  } catch (error) {
    throw new Error(getNetworkErrorMessage(error, fallbackMessage));
  }

  if (!response.ok) {
    const errorPayload = await safeReadJson(response);
    throw new Error(errorPayload?.message || fallbackMessage);
  }

  return response;
}

function getNetworkErrorMessage(error, fallbackMessage) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You are offline right now. Please reconnect to continue.';
  }

  if (error instanceof TypeError) {
    return 'Unable to reach the server right now. Please check your connection or try again in a moment.';
  }

  return fallbackMessage;
}

async function safeReadJson(response) {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}
